import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import Link from 'next/link'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Logo from '@/components/base/Logo'
import { useLenis, useLenisStore } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useTheme } from '@/contexts/theme/useTheme'
import { TRANSITION_LEAVE_DURATION } from '@/contexts/transition'
import { useLayoutStore } from '@/store/layout'
import { useRouterStore } from '@/store/router'

import { cn } from '@/lib/utils'

function Header() {
  const { strings } = useStore()
  const { lenis } = useLenis()
  const { theme } = useTheme()
  const [menuOpen] = useLayoutStore((state) => [state.menuOpen])
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () =>
        gsap.delayedCall(TRANSITION_LEAVE_DURATION, () =>
          setLayoutState({ menuOpen: false })
        )
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  useEffect(() => {
    if (!lenis) return
    if (menuOpen) {
      lenis.stop()
    } else {
      lenis.start()
    }
  }, [menuOpen, lenis])

  const lastScrollRef = useRef<number | null>(null)

  useEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => [state.scroll, state.direction],
      ([scroll, direction]) => {
        const currentScroll = scroll ?? 0
        const activeScroll = currentScroll > 200

        // Aggiorna lo sfondo dell'header in base allo scroll
        setLayoutState({ headerBackground: activeScroll })

        if (activeScroll) {
          if (direction === 1) {
            // Scrolling verso il basso: applica un threshold di 20px
            if (lastScrollRef.current === null) {
              // Imposta il punto di riferimento se non è ancora definito
              lastScrollRef.current = currentScroll
            } else if (currentScroll - lastScrollRef.current >= 20) {
              setLayoutState({ headerHide: true })
              // Aggiorna il riferimento per futuri controlli
              lastScrollRef.current = currentScroll
            }
          } else {
            // Scrolling verso l'alto: mostra l'header solo se lo scroll verso l'alto supera i 20px
            if (lastScrollRef.current === null) {
              lastScrollRef.current = currentScroll
            } else if (lastScrollRef.current - currentScroll >= 20) {
              setLayoutState({ headerHide: false })
              lastScrollRef.current = currentScroll
            }
          }
        } else {
          // Se lo scroll non è "attivo", mostra l'header e resetta il riferimento
          setLayoutState({ headerHide: false })
          lastScrollRef.current = null
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  return (
    <header
      className={cn([
        'fixed left-0 right-0 top-0 z-header h-header-height',
        'transition-all duration-500 ease-out-cubic',
        'mix-blend-exclusion'
      ])}
    >
      <Container>
        <Grid className="relative z-20">
          <div
            className={cn([
              'col-span-4 flex justify-center md:col-span-12',
              'transition-transform duration-2200 ease-out-expo'
            ])}
            data-header-span
          >
            <Link
              className="pt-[3.4rem]"
              href={strings?.header_settings?.website_url}
              target="_blank"
            >
              <Logo theme={menuOpen ? 'light' : theme} />
            </Link>
          </div>
        </Grid>
      </Container>
    </header>
  )
}

export default Header
